import { Vue, Component, Prop } from "vue-property-decorator";

import Checkbox from "@/components/inputs/Checkbox.vue";

import {
  ExtraKeys,
  HeaderKeys,
  ProductKeys,
} from "@/store/account/orders/enums";
import {
  OrderHeader,
  OrderItem,
  SimpleValue,
} from "@/store/account/orders/types";

import "./Row.scss";
import { calculateTotal, isDiscounted } from "@/store/cart/utils";
import { pricesModule } from "@/plugins/store-accessors";

@Component({ components: { Checkbox } })
export default class Row extends Vue {
  @Prop({ type: Array, default: () => [] })
  readonly headers!: OrderHeader[];

  @Prop({ type: Object, default: () => ({}) })
  readonly row!: OrderItem;

  @Prop({ type: String, default: "" })
  readonly active!: HeaderKeys;

  @Prop({ type: Array, default: () => [] })
  readonly selected!: number[];

  get sums() {
    return calculateTotal(this.row as any, true, true);
  }

  get isOnDiscount() {
    return this.row.promo_price && this.row.promo_stock;
  }

  get isSelected() {
    return !!this.selected.find((id) => id === this.row.id);
  }

  get allDiscounted() {
    return this.isOnDiscount && this.row.quantity < this.row.promo_stock;
  }

  get hidePrice() {
    return pricesModule.getHidePrice;
  }

  renderCells() {
    return this.headers.map((cell, ind) => {
      switch (cell.key) {
        case ExtraKeys.product:
          return (
            <td key={ind}>
              {this.renderProductCell()}
              {this.renderCellDiscount()}
            </td>
          );
        case ProductKeys.quantity:
          return this.renderCellQuantity();
        case ProductKeys.price:
          return this.renderCellPrice();
        case ProductKeys.total:
          return this.renderCellTotal();
        default:
          return this.renderCell(ind, cell.key, this.row[cell.key]);
      }
    });
  }

  renderCell(index: number, key: ProductKeys, content: SimpleValue) {
    const color = this.row.colors?.[key];
    const units = key === "quantity" ? "ед" : "дн";
    const keyCell = key === "quantity" || key === "reserved_days";
    const showDoubleCell =
      key !== "sku" && key !== "price" && key !== "total" && key !== "stock";
    return (
      <td key={index} class={`cell-${key} test`}>
        <span
          style={color ? { color: `${color} !important` } : null}
          class={this.active === key && "cell-active"}
        >
          {content} {keyCell ? units : null}
        </span>

        {/* TODO: Временный костыль на два дублирующий блока по макету, пока нет информации о инфе которая будет прилетать */}
        {this.sums.left && showDoubleCell ? (
          <div class="cell-double-info">
            <span
              style={color ? { color: `${color} !important` } : null}
              class={this.active === key && "cell-active"}
            >
              {content} {keyCell ? units : null}
            </span>
            <span
              style={color ? { color: `${color} !important` } : null}
              class={this.active === key && "cell-active"}
            >
              {content} {keyCell ? units : null}
            </span>
          </div>
        ) : null}
      </td>
    );
  }

  renderCellTotal() {
    const color = this.row.colors?.[ProductKeys.total];
    return (
      <td class={`cell-total`}>
        {!this.hidePrice && (
          <span
            style={color ? { color: `${color} !important` } : null}
            class={this.active === ProductKeys.total && "cell-active"}
          >
            {this.sums?.totals?.length
              ? `${this.row.total} ₽`
              : ""}
          </span>
        )}
        {this.sums.left && !this.hidePrice ? (
          <div class="cell-double-info">
            <span
              style={color ? { color: `${color} !important` } : null}
              class={this.active === ProductKeys.total && "cell-active"}
            >
              {`${this.sums.totals[1]?.toLocaleString()} ₽`}
            </span>
            <span
              style={color ? { color: `${color} !important` } : null}
              class={this.active === ProductKeys.total && "cell-active"}
            >
              {`${this.sums.totals[0]?.toLocaleString()} ₽`}
            </span>
          </div>
        ) : null}
        {!this.hidePrice ? (
          <div class="cell-double-info">
            {this.row.custom_total_price ? (
              <span>
                {`${this.row.custom_total_price.toLocaleString()} ₽`}
              </span>
            ) : null}

            {this.row.margin ? <span>{`${this.row.margin} %`}</span> : null}

            {this.row.benefit ? (
              <span>{`${this.row.benefit.toLocaleString()} ₽`}</span>
            ) : null}
          </div>
        ) : null}
      </td>
    );
  }
  renderCellPrice() {
    const color = this.row.colors?.[ProductKeys.price];
    return (
      <td class={`cell-price`}>
        {!this.hidePrice && (
          <span
            style={color ? { color: `${color} !important` } : null}
            class={[
              this.active === ProductKeys.price && "cell-active",
              this.allDiscounted && "cell-promo-price",
            ]}
          >
            {!this.sums.left ? `${(Number(this.row.total) / Number(this.row.quantity)).toLocaleString()} ₽` : ""}
          </span>
        )}

        {this.sums.left && !this.hidePrice ? (
          <div class="cell-double-info">
            <span
              style={color ? { color: `${color} !important` } : null}
              class={[
                this.active === ProductKeys.price && "cell-active",
                "cell-promo-price",
              ]}
            >
              {`${this.row.promo_price.toLocaleString()} ₽`}
            </span>
            <span
              style={color ? { color: `${color} !important` } : null}
              class={this.active === ProductKeys.price && "cell-active"}
            >
              {`${this.row.price.toLocaleString()} ₽`}
            </span>
          </div>
        ) : null}

        {!this.hidePrice ? (
          <div class="cell-double-info">
            {this.row.custom_total_price ? <span>Сумма продажи:</span> : null}

            {this.row.margin ? <span>Наценка:</span> : null}

            {this.row.benefit ? <span>Выгода:</span> : null}
          </div>
        ) : null}
      </td>
    );
  }
  renderCellQuantity() {
    const color = this.row.colors?.[ProductKeys.quantity];
    return (
      <td class={`cell-quantity`}>
        <span
          style={color ? { color: `${color} !important` } : null}
          class={this.active === ProductKeys.quantity && "cell-active"}
        >
          {`${this.row.quantity} ед`}
        </span>
        {this.sums.left ? (
          <div class="cell-double-info">
            <span
              style={color ? { color: `${color} !important` } : null}
              class={this.active === ProductKeys.quantity && "cell-active"}
            >
              {`${this.row.promo_stock} ед`}
            </span>
            <span
              style={color ? { color: `${color} !important` } : null}
              class={this.active === ProductKeys.quantity && "cell-active"}
            >
              {`${this.sums.left} ед`}
            </span>
          </div>
        ) : null}
      </td>
    );
  }

  renderProductCell() {
    return (
      <a
        href={`/product/${this.row.id}`}
        class={[
          "product-cell",
          this.active === ExtraKeys.product && "cell-active",
        ]}
        target="_blank"
      >
        {this.allDiscounted ? <span class="cell-tag">Акция</span> : null}
        <img src={this.row.image?.url} alt={this.row.image?.title} />
        <span class="product-name">{this.row.name}</span>
      </a>
    );
  }

  renderCellDiscount() {
    if (this.sums.left) {
      return (
        <div class="cell-double-info">
          <span>
            {this.isOnDiscount ? <span class="cell-tag">Акция</span> : null}
            Товары по акции
          </span>
          <span>Товары без акции</span>
        </div>
      );
    }
  }

  renderCheckbox() {
    return (
      <div class="table-checkbox-wrap">
        <checkbox
          value={this.isSelected}
          returnValue={(value: boolean) =>
            this.$emit("select", { id: this.row.id, value })
          }
          class="table-checkbox"
        />
      </div>
    );
  }

  render() {
    return (
      <tr class={["table-row"]}>
        <td>{this.renderCheckbox()}</td>
        {this.renderCells()}
      </tr>
    );
  }
}
